import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { listOpenAttempts, OPEN_ATTEMPTS_QUERY_KEY } from './continue-api';
import s from './ContinueCard.module.css';
import { ResumeAttemptRow } from './ResumeAttemptRow';

/** Rows shown inline before the card defers to the full list. */
const PREVIEW_LIMIT = 2;

/**
 * "You left something open" on the student home.
 *
 * Renders NOTHING when there is nothing unfinished — an empty state here would
 * be a permanent reminder of an absence, and the home page already has three
 * other sections doing real work. Everything past the first couple of rows
 * goes to /student/continue rather than growing the card.
 */
export function ContinueCard(): JSX.Element | null {
  const query = useQuery({
    queryKey: OPEN_ATTEMPTS_QUERY_KEY,
    queryFn: listOpenAttempts,
    refetchOnWindowFocus: true,
  });

  const rows = query.data ?? [];
  // A failed load is silent for the same reason: this card is an offer, not an
  // obligation, and the full page reports the error properly.
  if (query.isPending || query.isError || rows.length === 0) return null;

  const shown = rows.slice(0, PREVIEW_LIMIT);
  const hidden = rows.length - shown.length;

  return (
    <section className={s.root} aria-labelledby="continue-card-title">
      <header className={s.head}>
        <h2 id="continue-card-title" className={s.title}>
          Pick up where you left off
        </h2>
        <Link to="/student/continue" className={s.seeAll}>
          {hidden > 0 ? `See all ${String(rows.length)} →` : 'See all →'}
        </Link>
      </header>

      <ul className={s.list}>
        {shown.map((row) => (
          <ResumeAttemptRow key={row.attempt_id} row={row} compact />
        ))}
      </ul>
    </section>
  );
}
